import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../../api/axiosInstance'
import Swal from 'sweetalert2'

const COLORS = { gray: '#5B6B79', blue: '#96C5F7', green: '#2CA87F', red: '#9E2B25' }
const thStyle = { fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 10px', background: '#f8f9fb', borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap' }
const tdStyle = { fontSize: 13, color: '#2d3748', padding: '7px 10px', verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5' }
const inputStyle = { width: '100%', height: 36, borderRadius: 7, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }
const labelStyle = { fontSize: 11, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 4 }

const SearchBox = ({ value, onChange }) => (
  <div style={{ position: 'relative', maxWidth: 260 }}>
    <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: COLORS.gray, fontSize: 14, pointerEvents: 'none' }} />
    <input type="text" value={value} onChange={onChange} placeholder="Search..." style={{ paddingLeft: 32, paddingRight: 10, height: 34, fontSize: 13, border: '1px solid #dde3eb', borderRadius: 8, width: '100%', outline: 'none', color: COLORS.gray }} />
  </div>
)

const emptyForm = { test_name: '', units: '', min_range: '', max_range: '', methodology: '', value_type: 'numeric', reference_text: '' }
const fmt = val => (val !== null && val !== undefined && val !== '' ? parseFloat(val).toFixed(2) : '—')

export default function LabTests() {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  const fetchItems = () => {
    setIsLoading(true)
    axiosInstance.get('getAllLabTests')
      .then(res => setItems(res.data?.data || []))
      .catch(() => Swal.fire('Error', 'Failed to load lab tests', 'error'))
      .finally(() => setIsLoading(false))
  }
  useEffect(() => { fetchItems() }, [])

  const filtered = items.filter(i =>
    i.test_name?.toLowerCase().includes(search.toLowerCase()) ||
    i.units?.toLowerCase().includes(search.toLowerCase()) ||
    i.methodology?.toLowerCase().includes(search.toLowerCase())
  )

  const openAdd = () => { setEditItem(null); setForm(emptyForm); setModalOpen(true) }
  const openEdit = item => {
    setEditItem(item)
    setForm({ test_name: item.test_name || '', units: item.units || '', min_range: item.min_range ?? '', max_range: item.max_range ?? '', methodology: item.methodology || '', value_type: item.value_type || 'numeric', reference_text: item.reference_text || '' })
    setModalOpen(true)
  }
  const closeModal = () => { setModalOpen(false); setEditItem(null); setForm(emptyForm) }
  const setField = (name, value) => setForm(f => ({ ...f, [name]: value }))

  const handleSave = async () => {
    if (!form.test_name.trim()) { Swal.fire('Validation', 'Test name is required', 'warning'); return }
    setSaving(true)
    try {
      const isText = form.value_type === 'text'
      const payload = {
        test_name: form.test_name,
        units: form.units || null,
        min_range: !isText && form.min_range !== '' ? parseFloat(form.min_range) : null,
        max_range: !isText && form.max_range !== '' ? parseFloat(form.max_range) : null,
        methodology: form.methodology || null,
        value_type: form.value_type,
        reference_text: isText ? (form.reference_text || null) : null,
      }
      if (editItem) {
        await axiosInstance.put(`updateLabTest/${editItem.id}`, payload)
      } else {
        await axiosInstance.post('createLabTest', payload)
      }
      Swal.fire('Success', editItem ? 'Updated successfully' : 'Added successfully', 'success')
      closeModal(); fetchItems()
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save', 'error')
    } finally { setSaving(false) }
  }

  const handleDelete = id => {
    Swal.fire({ title: 'Are you sure?', text: 'This lab test will be deleted.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes, delete it!' })
      .then(result => {
        if (result.isConfirmed)
          axiosInstance.delete(`deleteLabTest/${id}`)
            .then(() => { Swal.fire('Deleted!', 'Lab test deleted.', 'success'); fetchItems() })
            .catch(() => Swal.fire('Error', 'Failed to delete', 'error'))
      })
  }

  const TypeBadge = ({ type }) => (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: type === 'text' ? '#fef3c7' : `${COLORS.green}22`, color: type === 'text' ? '#92400e' : COLORS.green }}>
      {type === 'text' ? 'Text' : 'Numeric'}
    </span>
  )

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => navigate(-1)} title="Go back" style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, marginRight: 4, flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }} onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}><i className="ti ti-arrow-left" /></button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="ti ti-microscope" style={{ color: COLORS.blue, fontSize: 17 }} /></div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Lab Tests</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage laboratory test definitions</div>
            </div>
          </div>
          <div style={{ padding: '10px 20px', borderBottom: '1px solid #e8ecf0', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '5px 12px' }} onClick={openAdd}><i className="ti ti-plus" style={{ fontSize: 15 }} /> Add Lab Test</button>
          </div>
          <div style={{ padding: '14px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <SearchBox value={search} onChange={e => setSearch(e.target.value)} />
              <span style={{ fontSize: 11, color: COLORS.gray }}>{filtered.length} test{filtered.length !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: 50, borderLeft: `3px solid ${COLORS.blue}` }}>#</th>
                    <th style={thStyle}>Test Name</th>
                    <th style={{ ...thStyle, width: 90 }}>Type</th>
                    <th style={{ ...thStyle, width: 90 }}>Units</th>
                    <th style={thStyle}>Range / Reference</th>
                    <th style={thStyle}>Methodology</th>
                    <th style={{ ...thStyle, width: 100, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', padding: '32px 0', color: COLORS.gray }}><i className="ti ti-loader-2" style={{ fontSize: 22, display: 'block', marginBottom: 6, opacity: 0.5 }} />Loading...</td></tr>}
                  {!isLoading && filtered.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', padding: '32px 0', color: COLORS.gray }}><i className="ti ti-database-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />No lab tests found</td></tr>}
                  {!isLoading && filtered.map((item, i) => (
                    <tr key={item.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }} onMouseEnter={e => e.currentTarget.style.background = `${COLORS.blue}0d`} onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}>
                      <td style={tdStyle}>{i + 1}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{item.test_name}</td>
                      <td style={tdStyle}><TypeBadge type={item.value_type} /></td>
                      <td style={{ ...tdStyle, color: COLORS.gray }}>{item.units || '—'}</td>
                      <td style={{ ...tdStyle, color: COLORS.gray, fontSize: 12 }}>
                        {item.value_type === 'text' ? (item.reference_text || '—') : `${fmt(item.min_range)} – ${fmt(item.max_range)}`}
                      </td>
                      <td style={{ ...tdStyle, color: COLORS.gray, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.methodology || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button onClick={() => openEdit(item)} title="Edit" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer', background: `${COLORS.blue}22`, color: COLORS.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }} onMouseEnter={e => e.currentTarget.style.background = COLORS.blue + '44'} onMouseLeave={e => e.currentTarget.style.background = COLORS.blue + '22'}><i className="ti ti-edit" /></button>
                          <button onClick={() => handleDelete(item.id)} title="Delete" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer', background: `${COLORS.red}18`, color: COLORS.red, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }} onMouseEnter={e => e.currentTarget.style.background = COLORS.red + '33'} onMouseLeave={e => e.currentTarget.style.background = COLORS.red + '18'}><i className="ti ti-trash" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }} onClick={closeModal}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 580, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', marginBottom: 40 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="ti ti-microscope" style={{ color: COLORS.blue, fontSize: 18 }} /></div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>{editItem ? 'Edit Lab Test' : 'Add Lab Test'}</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>{editItem ? 'Update test definition' : 'Add a new laboratory test'}</div>
              </div>
              <button onClick={closeModal} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}><i className="ti ti-x" /></button>
            </div>

            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Row 1: Test Name + Value Type */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Test Name <span style={{ color: '#e53e3e' }}>*</span></label>
                  <input style={inputStyle} value={form.test_name} onChange={e => setField('test_name', e.target.value)} placeholder="e.g. Complete Blood Count" onFocus={e => e.target.style.borderColor = COLORS.blue} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>
                <div>
                  <label style={labelStyle}>Value Type</label>
                  <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.value_type} onChange={e => setField('value_type', e.target.value)} onFocus={e => e.target.style.borderColor = COLORS.blue} onBlur={e => e.target.style.borderColor = '#dde3eb'}>
                    <option value="numeric">Numeric</option>
                    <option value="text">Text</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Units + Range/Reference */}
              <div style={{ display: 'grid', gridTemplateColumns: form.value_type === 'text' ? '120px 1fr' : '120px 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Units</label>
                  <input style={inputStyle} value={form.units} onChange={e => setField('units', e.target.value)} placeholder="e.g. mg/dL" onFocus={e => e.target.style.borderColor = COLORS.blue} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>
                {form.value_type === 'text' ? (
                  <div>
                    <label style={labelStyle}>Reference Text</label>
                    <input style={inputStyle} value={form.reference_text} onChange={e => setField('reference_text', e.target.value)} placeholder="e.g. Negative, Clear" onFocus={e => e.target.style.borderColor = COLORS.blue} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                  </div>
                ) : (
                  <>
                    <div>
                      <label style={labelStyle}>Min Range</label>
                      <input style={inputStyle} type="number" step="0.01" value={form.min_range} onChange={e => setField('min_range', e.target.value)} placeholder="e.g. 3.50" onFocus={e => e.target.style.borderColor = COLORS.blue} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                    </div>
                    <div>
                      <label style={labelStyle}>Max Range</label>
                      <input style={inputStyle} type="number" step="0.01" value={form.max_range} onChange={e => setField('max_range', e.target.value)} placeholder="e.g. 10.00" onFocus={e => e.target.style.borderColor = COLORS.blue} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                    </div>
                  </>
                )}
              </div>

              {/* Row 3: Methodology */}
              <div>
                <label style={labelStyle}>Methodology</label>
                <textarea value={form.methodology} onChange={e => setField('methodology', e.target.value)} placeholder="Enter methodology description..." rows={3} style={{ width: '100%', borderRadius: 7, border: '1.5px solid #dde3eb', padding: '8px 10px', fontSize: 13, color: '#1a202c', outline: 'none', resize: 'vertical' }} onFocus={e => e.target.style.borderColor = COLORS.blue} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 24px', borderTop: '1px solid #e8ecf0' }}>
              <button onClick={closeModal} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ padding: '7px 18px', fontSize: 13 }}>
                {saving && <i className="ti ti-loader-2" style={{ fontSize: 14 }} />}{saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
